<!-- @format -->
<template>
	<el-form
		ref="formRef"
		:model="searchFormDatas"
		label-position="right"
		label-width="auto"
		class="pc-table-header"
		@submit.prevent
	>
		<!-- 全局搜索 -->
		<div v-if="canCommentSearch" class="pc-table-header-comment">
			<slot name="search-comment-front"></slot>
			<el-input
				v-model="searchFormDatas[COMMENT_SEARCH]"
				class="pc-table-header-comment-search"
				:placeholder="commentSearchPlaceHolder || '请输入查询条件'"
				clearable
				@keyup.enter="onSearch"
				@clear="onSearch"
			>
			</el-input>
			<el-button
				v-if="canMoreSearch"
				link
				style="margin-left: 8px; margin-bottom: 16px"
				@click="searchState.showMoreSearchList = !searchState.showMoreSearchList"
			>
				更多
				<el-icon style="margin-left: 4px">
					<CaretTop v-if="searchState.showMoreSearchList" />
					<CaretBottom v-else />
				</el-icon>
			</el-button>
			<slot name="search-comment-backend"></slot>
		</div>

		<!-- 更多筛选 -->
		<el-row v-show="canCommentSearch ? searchState.showMoreSearchList : true" :gutter="16">
			<!-- 查询条件 -->
			<el-col v-for="item in showSearchList" :key="item.code" :span="SEARCH_SPAN_MAP[item.type] || searchSpan">
				<el-form-item :label="item.label" :prop="item.code">
					<slot name="search-cpt-front" v-bind="item"></slot>
					<slot
						name="search-cpt"
						v-bind="item"
						:model-value="searchFormDatas[item.code]"
						:update:model-value="(e:any) => (searchFormDatas[item.code] = e)"
					>
						<component :is="getComponent(item.type)" v-model="searchFormDatas[item.code]" v-bind="item.pcProps" />
					</slot>
					<slot name="search-cpt-backend" v-bind="item"></slot>
				</el-form-item>
			</el-col>

			<!-- 查询按钮 -->
			<el-col :span="searchBtnSpan" style="text-align: end; margin-bottom: 16px">
				<el-button
					v-if="!canCommentSearch && canMoreSearch"
					link
					@click="searchState.showMoreSearchList = !searchState.showMoreSearchList"
				>
					更多
					<el-icon style="margin-left: 4px">
						<CaretTop v-if="searchState.showMoreSearchList" />
						<CaretBottom v-else />
					</el-icon>
				</el-button>
				<template v-if="showSearchList?.length">
					<el-button type="primary" @click="onSearch"> 查询 </el-button>
					<el-button plain @click="onReset"> 重置 </el-button>
				</template>
				<column-setting
					v-if="moreSearchList"
					ref="checkMoreSearchRef"
					:default-column="searchState.checkSearchList"
					:list="moreSearchList"
				>
					<el-button
						v-if="canAddMoreSearch"
						plain
						style="margin-left: 8px"
						@click="searchState.showMoreSearchList = true"
					>
						自定义
					</el-button>
				</column-setting>
			</el-col>
		</el-row>
	</el-form>
</template>

<script setup lang="ts">
	import ColumnSetting from './column-setting.vue';
	import type { FormInstance } from 'element-plus';
	import type { ComponentType } from './types';
	import { type CheckGroupList } from '@/components/pc-check-box-list/types';
	import { getComponent } from '@/components';
	import PcCheckBoxList from '@/components/pc-check-box-list/index.vue';

	interface Props {
		modelValue: Record<string, any>;
		searchSpan?: number; // 筛选条件span占比
		searchList?: ComponentType[]; // 查询条件字段列表
		moreSearchList?: CheckGroupList; // 更多筛选字段列表
		defaultCheckMoreSearchList?: string[]; // 默认选中的更多筛选字段
		defaultSearchValue?: Record<string, any>; // 查询条件默认值
		canMoreSearch?: boolean; // 可展开更多筛选框
		canAddMoreSearch?: boolean; // 可添加更多筛选字段
		canCommentSearch?: boolean; // 是否有全局搜索框
		commentSearchPlaceHolder?: string; // 全局搜索框提示信息
		tableRef?: any; //表格ref
	}

	interface SearchState {
		checkSearchList: string[];
		showMoreSearchList: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		searchList: () => [],
		searchSpan: 6,
	});

	const emit = defineEmits(['on-search', 'on-search-reset', 'update:modelValue']);

	const COMMENT_SEARCH = '$commentSearch$';

	const SEARCH_SPAN_MAP = {
		datetime: 12,
	};

	const formRef = ref<FormInstance | null>(null);
	const checkMoreSearchRef = ref<InstanceType<typeof PcCheckBoxList>>();

	const searchState = ref<SearchState>({
		checkSearchList: props.defaultCheckMoreSearchList || [], // 选中放到更多筛选中的字段
		showMoreSearchList: false, // 更多筛选展开状态
	});

	const searchFormDatas = computed({
		get() {
			return props.modelValue;
		},
		set(val) {
			emit('update:modelValue', val);
		},
	});

	// 带全局搜索或展开时才展示所有字段
	const showSearchList = computed(() =>
		searchState.value.showMoreSearchList || props.canCommentSearch
			? (props.searchList || []).concat((checkMoreSearchRef.value?.checkItemList as ComponentType[]) || [])
			: props.searchList,
	);

	// 查询按钮的偏移量
	const searchBtnSpan = computed(() => {
		const count = showSearchList.value.reduce((count, it) => {
			const itSpan = SEARCH_SPAN_MAP[it.type] || props.searchSpan;
			const spans = count + itSpan;
			if (spans === 24) {
				return 0;
			} else if (spans > 24) {
				return itSpan;
			}
			return spans;
		}, 0);
		return 24 - count;
	});

	const onSearch = () => {
		emit('on-search', searchFormDatas.value);
		props.tableRef.scrollTo({
			left: 0,
			top: 0,
			behavior: 'smooth',
		});
	};

	const onReset = () => {
		formRef.value?.resetFields();
		emit('on-search-reset');
		props.tableRef.scrollTo({
			left: 0,
			top: 0,
			behavior: 'smooth',
		});
	};

	watch(
		() => props.defaultCheckMoreSearchList,
		(newVal) => {
			searchState.value.checkSearchList = newVal || [];
		},
	);
</script>

<style scoped lang="scss">
	.pc-table-header {
		.pc-table-header-comment {
			display: flex;
			align-items: center;
			gap: 10px;

			.pc-table-header-comment-search {
				width: 400px;
				margin-bottom: 16px;

				:deep(.el-input__suffix) {
					cursor: pointer;
				}

				.pc-table-header__suffix-inner {
					height: 100%;
					width: 100%;
				}
			}
		}

		.pc-table-header-more-search-list {
			border: 1px solid var(--el-border-color);
			border-radius: var(--el-border-radius-base);
			margin-top: 10px;
			padding: 10px;

			.pc-table-header-more-search-add-btn {
				display: flex;
				flex-direction: row-reverse;
			}
		}
	}
</style>
